import * as React from 'react'
import { fromJSX } from 'widgets-for-react'
import { getClassNameModern } from '../../../../../../Shared/Frontend/utils/getClassNameFromStyle'
import type { DP } from '../../../drupal/blockDefinitions'
import { dummyData, randomTitle, randomWords } from '../../../utils/dummyData'
import { linkWrapper } from '../../../utils/link'
import type { BGColors, ModernBlock, SeoEvent } from '../../modernBlocks.state'
import { highlightBlockClasses, HighlightBlockLayout } from './highlightBlock.layout'
import type { HighlightBlockProps } from './highlightBlock.state'

export const highlightBlock: ModernBlock<HighlightBlockProps, any, DP.Highlight> = {
  kind: 'highlightBlock',
  render: (setRootState, rootState, blockIndex, blockProps, amountNested) =>
    fromJSX((updateBlock) => (
      <HighlightBlockLayout.Inner id={`highlightblock-${blockIndex}`} amountNested={amountNested!}>
        <HighlightBlockLayout.Content
          label={blockProps.label}
          title={blockProps.title}
          intro={blockProps.intro}
          text={blockProps.text}
          showPrimary={blockProps.showPrimary}
          showSecondary={blockProps.showSecondary}
          primaryAction={blockProps.primaryAction!}
          secondaryAction={blockProps.secondaryAction!}
          showActionAsLink={blockProps.showActionAsLink}
          bgColour={blockProps.bgColour}
        />
      </HighlightBlockLayout.Inner>
    )),
  adapters: {
    drupal: (dpBlock: DP.Highlight, blockIndex: number) => {
      const target = dpBlock.target.type[0].target_id
      if (target != 'highlight_block') return undefined

      return {
        title: dpBlock.target.field_title ? dpBlock.target.field_title[0]?.value : undefined,
        excludeFromPageMenu: dpBlock.target?.field_exclude_from_page_menu?.[0]?.value,
        text: dpBlock.target.field_body[0]?.processed,
        label: dpBlock.target.field_label ? dpBlock.target.field_label[0]?.value : undefined,
        intro: dpBlock.target.field_introduction ? dpBlock.target.field_introduction[0]?.value : undefined,
        bgColour: dpBlock.target.field_highlight_background_color
          ? dpBlock.target.field_highlight_background_color[0]?.value as BGColors
          : 'darkred',
        showPrimary: dpBlock.target.field_links[0] ? true : false,
        showSecondary: dpBlock.target.field_links[1] ? true : false,
        primaryAction: linkWrapper({
          title: dpBlock.target?.field_links ? dpBlock.target?.field_links[0]?.title! : '',
          url: dpBlock.target?.field_links ? dpBlock.target?.field_links[0]?.processed_url! : '',
          anchor: dpBlock.target?.field_links ? dpBlock.target?.field_links[0]?.options?.attributes?.id! : '',
        }),
        secondaryAction: linkWrapper({
          title: dpBlock.target?.field_links ? dpBlock.target?.field_links[1]?.title! : '',
          url: dpBlock.target?.field_links ? dpBlock.target?.field_links[1]?.processed_url! : '',
          anchor: dpBlock.target?.field_links ? dpBlock.target?.field_links[1]?.options?.attributes?.id! : '',
        }),
        anchorTitle: dpBlock.target.field_title ? dpBlock.target.field_title[0]?.value : '',
        showActionAsLink: dpBlock.target.field_link_type[0]?.value == true ? true : false,
        inviewState: false,
      }
    },
  },
  sampleExample: [
    {
      title: randomTitle(),
      label: randomWords(),
      intro: dummyData.intro,
      text: dummyData.typography,
      bgColour: 'lightred',
      anchorTitle: 'highlight-sample-1',
      showPrimary: true,
      showSecondary: true,
      primaryAction: dummyData.primaryButton,
      secondaryAction: dummyData.secondaryButton,
      showActionAsLink: false,
    },
    {
      title: randomTitle(),
      text: dummyData.wysiwyg,
      bgColour: 'red',
      anchorTitle: 'highlight-sample-2',
      showPrimary: true,
      showSecondary: false,
      primaryAction: dummyData.primaryButton,
      showActionAsLink: false,
    },
    {
      title: randomTitle(),
      text: dummyData.wysiwyg,
      bgColour: 'darkred',
      anchorTitle: 'highlight-sample-3',
      showPrimary: false,
      showSecondary: true,
      secondaryAction: dummyData.secondaryButton,
      showActionAsLink: false,
    },
    {
      title: randomTitle(),
      text: dummyData.wysiwyg,
      bgColour: 'lightgray',
      anchorTitle: 'highlight-sample-3',
      showPrimary: false,
      showSecondary: true,
      secondaryAction: dummyData.secondaryButton,
      showActionAsLink: false,
    },
    {
      title: randomTitle(),
      text: dummyData.wysiwyg,
      bgColour: 'lightblue',
      anchorTitle: 'highlight-sample-3',
      showPrimary: false,
      showSecondary: true,
      secondaryAction: dummyData.secondaryButton,
      showActionAsLink: false,
    },
  ],
  seoEvents: (props, blockIndex) =>
    [
      props.showPrimary && props.primaryAction
        ? {
            id: `primary_button`,
            event: 'onClick',
            category: 'text_block',
            label: window.location.pathname,
            action: 'primary_' + props.primaryAction.title,
          }
        : undefined,
      props.showSecondary && props.secondaryAction
        ? {
            id: `secondary_button`,
            event: 'onClick',
            category: 'text_block',
            label: window.location.pathname,
            action: 'secondary_' + props.secondaryAction.title,
          }
        : undefined,
    ].filter((i) => i != undefined) as SeoEvent[],
  inView: {
    showAs: 'section',
    threshold: 0.1,
    triggerOnce: true,
    inViewClassName: getClassNameModern(highlightBlockClasses, 'inview', 'inview'),
    updateState: (currentInviewState, currentBlockState) => {
      return {
        ...currentBlockState,
        inviewState: currentInviewState,
      }
    },
  },
  blockClassName: (props) => `
    ${getClassNameModern(highlightBlockClasses, 'block', 'mb-6')}
    ${props.bgColour ? HighlightBlockLayout.ColorSchemeClassName(props.bgColour) : 'bg-light'}
  `,
}
